import { UpperCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit, inject, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType } from '@azure/msal-browser';
import { AppInsightsService, StaticActions } from '@ct/data';
import { ENVIRONMENT, EnvironmentConfig, NameIdModel, PROJECT_PREFIX, VESSEL_TYPES } from '@ct/model';
import { Store } from '@ngrx/store';
import { NAVIGATION_HOME_ICON, StoAppHeaderComponent } from '@ngx-stoui/common';
import { NavDrawerComponent, Navigation } from '@ngx-stoui/drawer';
import { filter, take } from 'rxjs/operators';
import { ReleaseNotesComponent } from '../../../../release-notes/src/lib/release-notes.component';
import { NavigationCombined } from '../projects/type';

@Component({
  selector: 'ct-navigation',
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NAVIGATION_HOME_ICON, useValue: { svgIcon: 'tnt' } }],
  imports: [
    StoAppHeaderComponent,
    ReleaseNotesComponent,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    MatIcon,
    MatTooltip,
    NavDrawerComponent,
    UpperCasePipe,
  ],
})
export class NavigationComponent implements OnInit, AfterViewInit {
  private readonly proj = inject(PROJECT_PREFIX);
  readonly env = inject<EnvironmentConfig>(ENVIRONMENT, { optional: true });
  private readonly vesselTypes = inject<Map<string, NameIdModel>>(VESSEL_TYPES, { optional: true });
  private readonly msal = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly globalStore = inject(Store);
  private readonly appInsightsService = inject(AppInsightsService, { optional: true });

  readonly navigation = input<Array<NavigationCombined>>(undefined);
  @Input()
  user: AccountInfo | undefined;
  readonly hideReleaseNotes = input<boolean>(undefined);
  readonly navigate = output<string[]>();
  readonly logout = output();
  public isInitialized = false;
  public isOpen = false;
  breadcrumbRoot = {};
  homeBreadCrumbConfig = {
    command: () => this.openMenu(),
  };
  public breadcrumbs: any[] = [this.breadcrumbRoot];
  readonly feedbackMailto = toMailto(this.env?.FEEDBACK_EMAIL);

  constructor() {
    const proj = this.proj;

    this.breadcrumbRoot = {
      label: `${proj?.toUpperCase()} Cargo Tracker`,
      command: () => this.navigate.emit(['capture']),
    };
  }

  private _routeData: any;
  navigationItems: Array<Navigation>;
  collapsed = true;

  @Input() set routeData(value: any) {
    this._routeData = value;
    this.breadcrumbs = [this.breadcrumbRoot];
    if (value && value.breadCrumb) {
      this.breadcrumbs = [
        ...this.breadcrumbs,
        {
          label: value.breadCrumb,
          command: value.link ? () => this.navigate.emit([value.link]) : null,
        },
      ];
    }
  }

  ngOnInit(): void {
    // Auth is already handled by MSALRedirectFactory in app initializer
    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.user = accounts[0];
      this.msal.instance.setActiveAccount(this.user);
      this.loadStatics();
    } else {
      // Wait for login to complete
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((event) => event.eventType === EventType.LOGIN_SUCCESS),
          take(1),
        )
        .subscribe(() => {
          this.user = this.msal.instance.getAllAccounts()[0];
          this.msal.instance.setActiveAccount(this.user);
          this.loadStatics();
        });
    }
  }

  loadStatics() {
    this.globalStore.dispatch(StaticActions.LoadProducts());
    this.globalStore.dispatch(StaticActions.LoadCountries());
    this.globalStore.dispatch(StaticActions.LoadTerminals());
    this.globalStore.dispatch(StaticActions.LoadRegions());
    this.globalStore.dispatch(StaticActions.LoadCompanies());
    this.globalStore.dispatch(StaticActions.LoadVesselTypesSuccess({ vesselTypes: this.vesselTypes }));
    this.globalStore.dispatch(StaticActions.LoadVessels());
    if (this.appInsightsService) {
      this.appInsightsService.setAuthenticatedUser(this.user.username);
    }
  }

  ngAfterViewInit(): void {
    this.isInitialized = true;
  }

  openMenu() {
    this.isOpen = true;
  }

  openFeedback() {
    window.open(this.feedbackMailto, '_self');
  }
}

function toMailto(emails: string | undefined): string | null {
  const recipients = (emails ?? '')
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean);
  return recipients.length
    ? `mailto:${recipients.join(',')}?subject=${encodeURIComponent('CargoTracker feedback')}`
    : null;
}
